'use client';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import Header from '../../components/Header';
import Load from '../../components/Load';
import LeagueFilters, { LeagueFiltersState } from '../../components/admin/LeagueFilters';
import LeagueTable from '../../components/admin/LeagueTable';
import useLiga from '../../hooks/useLiga';
import type { Liga, LigaConnection } from '../../types/liga';
import { Alert, Snackbar } from '@mui/material';
import CreateLeagueDialog from '../../components/admin/CreateLeagueDialog';
import ConfirmDeleteLeagueDialog from '../../components/admin/ConfirmDeleteLeagueDialog';
import LeagueImageDialog from '../../components/admin/LeagueImageDialog';


const PAGE_SIZE = 20;

interface PaginationState {
  page: number;
  size: number;
  totalPages: number;
}

const Ligas: React.FC = () => {
  const {
    buscarFiltradas,
    criar,
    deletar,
    loading,
    error,
  } = useLiga();

  const [filters, setFilters] = useState<LeagueFiltersState>({
    nome: '',
    username: '',
    iuguConfigId: '',
  });
  const [ligas, setLigas] = useState<Liga[]>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    page: 0,
    size: PAGE_SIZE,
    totalPages: 0,
  });

  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Liga | null>(null);
  const [imageTarget, setImageTarget] = useState<Liga | null>(null);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const handleCloseSnackbar = () =>
    setSnackbar((s) => ({ ...s, open: false }));

  const loadLigas = useCallback(async () => {
    try {
      const connection: LigaConnection = await buscarFiltradas(
        {
          nome: filters.nome || null,
          username: filters.username || null,
          iuguConfigId: filters.iuguConfigId || null,
        },
        pagination.page,
        pagination.size,
        'nome',
        'ASC',
      );
      setLigas(connection.items);
      setPagination((prev) => ({
        ...prev,
        page: connection.page,
        size: connection.size,
        totalPages: connection.totalPages,
      }));
    } catch (err: any) {
      const msg = err?.message || 'Erro ao carregar ligas.';
      setSnackbar({
        open: true,
        message: msg,
        severity: 'error',
      });
    }
  }, [buscarFiltradas, filters, pagination.page, pagination.size]);

  useEffect(() => {
    loadLigas();
  }, [loadLigas]);

  const handleFilterChange = (f: LeagueFiltersState) => {
    setPagination((prev) => ({ ...prev, page: 0 }));
    setFilters(f);
  };

  const handleOpenCreateDialog = () => setOpenCreateDialog(true);
  const handleCloseCreateDialog = () => setOpenCreateDialog(false);

  const handleCreateLeague = async (input: any) => {
    try {
      const liga = await criar(input);
      setSnackbar({
        open: true,
        message: 'Liga criada com sucesso.',
        severity: 'success',
      });
      await loadLigas();
      return liga;
    } catch (err: any) {
      const msg = err?.message || 'Erro ao criar liga.';
      setSnackbar({
        open: true,
        message: msg,
        severity: 'error',
      });
      throw err;
    }
  };

  const handleDeleteRequest = (liga: Liga) => {
    setDeleteTarget(liga);
  };

  const handleCancelDelete = () => {
    setDeleteTarget(null);
  };

  const handleManageImages = (liga: Liga) => {
    setImageTarget(liga);
  };

  const handleCloseImageDialog = () => {
    setImageTarget(null);
  };


  const handleImageUpdate = (updatedLiga: Liga) => {
    setLigas(prev => prev.map(liga => 
      liga.id === updatedLiga.id ? updatedLiga : liga
    ));
    setSnackbar({
      open: true,
      message: 'Imagem atualizada com sucesso.',
      severity: 'success',
    });
  };

  const handleConfirmDelete = async (id: string) => {
    try {
      await deletar(id);
      setDeleteTarget(null);
      setSnackbar({
        open: true,
        message: 'Liga deletada com sucesso.',
        severity: 'success',
      });
      await loadLigas();
    } catch (err: any) {
      const msg = err?.message || 'Erro ao deletar liga.';
      setSnackbar({
        open: true,
        message: msg,
        severity: 'error',
      });
      throw err;
    }
  };

  const handlePrevPage = () => {
    setPagination((prev) => ({
      ...prev,
      page: Math.max(prev.page - 1, 0),
    }));
  };

  const handleNextPage = () => {
    setPagination((prev) => ({
      ...prev,
      page: Math.min(prev.page + 1, prev.totalPages - 1),
    }));
  };

  return (
    <Fragment>
      <div className="main-wrapper">
        <Header />
        <div className="main-content theme-dark-bg right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="middle-wrap">
                <div className="card w-100 border-0 bg-white shadow-xs p-0 mb-4">
                  <div className="card-body p-4 w-100 bg-current border-0 d-flex rounded-3 align-items-center">
                    <button
                      type="button"
                      className="btn btn-link text-white p-0 d-inline-block mt-2 me-4"
                      onClick={() => window.history.back()}
                    >
                      <i className="ti-arrow-left font-sm"></i>
                    </button>
                    <h4 className="font-xs text-white fw-600 mb-0 mt-2 flex-grow-1">
                      Ligas
                    </h4>
                    <button
                      className="btn btn-primary"
                      onClick={handleOpenCreateDialog}
                    >
                      Criar Nova Liga
                    </button>
                  </div>
                </div>

                <LeagueFilters
                  filters={filters}
                  onChange={handleFilterChange}
                />

                {loading && <Load />}
                {error && !loading && (
                  <p className="text-danger">
                    {error || 'Erro ao carregar ligas.'}
                  </p>
                )}

                <LeagueTable
                  ligas={ligas}
                  loading={loading}
                  onDelete={handleDeleteRequest}
                  onManageImages={handleManageImages}
                />

                {pagination.totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handlePrevPage}
                      disabled={pagination.page === 0}
                    >
                      Anterior
                    </button>
                    <span>
                      Página {pagination.page + 1} de {pagination.totalPages}
                    </span>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleNextPage}
                      disabled={
                        pagination.page >= pagination.totalPages - 1
                      }
                    >
                      Próxima
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <CreateLeagueDialog
        open={openCreateDialog}
        onClose={handleCloseCreateDialog}
        onCreate={handleCreateLeague}
      />

      <ConfirmDeleteLeagueDialog
        open={!!deleteTarget}
        league={deleteTarget}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />

      <LeagueImageDialog
        open={!!imageTarget}
        liga={imageTarget}
        onClose={handleCloseImageDialog}
        onUpdate={handleImageUpdate}
      />

    </Fragment>
  );
};

export default Ligas;







